import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle, Lock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const OnboardingWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState({});
  const [onboardingData, setOnboardingData] = useState({});
  const [stepData, setStepData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Onboarding steps configuration
  const steps = [
    {
      id: 'welcome',
      title: 'Welcome & Overview',
      required: false,
      type: 'info'
    },
    {
      id: 'responsibilities',
      title: 'Role Responsibilities & Boundaries',
      required: true,
      type: 'acknowledgment'
    },
    {
      id: 'nda',
      title: 'Confidentiality & Non-Disclosure Agreement',
      required: true,
      type: 'signature'
    },
    {
      id: 'security',
      title: 'Security & Access Rules',
      required: true,
      type: 'acknowledgment'
    },
    {
      id: 'communication',
      title: 'Communication & Escalation Rules',
      required: true,
      type: 'acknowledgment'
    },
    {
      id: 'walkthrough',
      title: 'System Walkthrough',
      required: true,
      type: 'checklist'
    },
    {
      id: 'payment_info',
      title: 'Payment Information',
      required: true,
      type: 'form'
    },
    {
      id: 'availability',
      title: 'Availability & Working Hours',
      required: true,
      type: 'form'
    },
    {
      id: 'tax_compliance',
      title: 'Tax & Compliance',
      required: false,
      type: 'form'
    },
    {
      id: 'final_confirmation',
      title: 'Final Confirmation',
      required: true,
      type: 'confirmation'
    }
  ];

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/onboarding/status`, {
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setProgress(data.onboarding_progress || {});
        setOnboardingData(data.onboarding_data || {});
        
        // Find first incomplete required step
        const firstIncomplete = steps.findIndex(step => {
          const stepProgress = data.onboarding_progress?.[step.id];
          return step.required && !stepProgress?.completed;
        });
        
        if (firstIncomplete !== -1) {
          setCurrentStep(firstIncomplete);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setLoading(false);
    }
  };

  const handleCompleteStep = async () => {
    const step = steps[currentStep];
    setError('');

    // Validate step before completing
    if (!validateStep(step)) {
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          step_id: step.id,
          data: stepData[step.id] || {}
        })
      });

      if (res.ok) {
        // Update local progress
        setProgress({
          ...progress,
          [step.id]: {
            completed: true,
            completed_at: new Date().toISOString()
          }
        });

        // Move to next step or complete
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
          setError('');
        } else {
          await handleFinishOnboarding();
        }
      } else {
        setError('Failed to save step. Please try again.');
      }
    } catch (err) {
      console.error('Error completing step:', err);
      setError('Network error. Please try again.');
    }
  };

  const validateStep = (step) => {
    const data = stepData[step.id] || {};

    switch (step.id) {
      case 'responsibilities':
        if (!data.checkbox1 || !data.checkbox2 || !data.checkbox3 || !data.checkbox4 || !data.fullName) {
          setError('Please complete all required acknowledgments and enter your full name.');
          return false;
        }
        break;
      
      case 'nda':
        if (!data.ndaAgreed || !data.legalName) {
          setError('Please agree to the NDA and provide your legal name.');
          return false;
        }
        break;
      
      case 'security':
        if (!data.checkbox1 || !data.checkbox2 || !data.checkbox3 || !data.checkbox4) {
          setError('Please acknowledge all security rules.');
          return false;
        }
        break;
      
      case 'communication':
        if (!data.agreed) {
          setError('Please acknowledge communication guidelines.');
          return false;
        }
        break;
      
      case 'walkthrough':
        if (!data.completed) {
          setError('Please mark the walkthrough as complete.');
          return false;
        }
        break;
      
      case 'payment_info':
        if (!data.paymentMethod || !data.legalName) {
          setError('Please provide payment method and legal name.');
          return false;
        }
        if ((data.paymentMethod === 'paypal' || data.paymentMethod === 'cashapp') && !data.handle) {
          setError('Please provide your PayPal email or Cash App tag.');
          return false;
        }
        break;
      
      case 'availability':
        if (!data.timezone || !data.workingHours || !data.daysAvailable || data.daysAvailable.length === 0) {
          setError('Please provide your timezone, working hours, and at least one available day.');
          return false;
        }
        if (!data.emergencyAvailable) {
          setError('Please indicate your emergency availability.');
          return false;
        }
        break;
      
      case 'final_confirmation':
        if (!data.checkbox1 || !data.checkbox2) {
          setError('Please confirm all final acknowledgments.');
          return false;
        }
        break;
    }

    return true;
  };

  const handleFinishOnboarding = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/onboarding/finish`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        onComplete();
      }
    } catch (err) {
      console.error('Error finishing onboarding:', err);
    }
  };

  const updateStepData = (stepId, field, value) => {
    setStepData({
      ...stepData,
      [stepId]: {
        ...stepData[stepId],
        [field]: value
      }
    });
  };

  const isStepComplete = (stepId) => {
    return progress[stepId]?.completed || false;
  };

  const canSkipStep = () => {
    return !steps[currentStep].required;
  };

  const handleSkipStep = () => {
    if (canSkipStep() && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const requiredStepsCompleted = steps
    .filter(s => s.required)
    .every(s => isStepComplete(s.id));

  const totalSteps = steps.length;
  const completedSteps = Object.values(progress).filter(p => p.completed).length;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{ color: '#FFFFFF', fontSize: '16px' }}>Loading onboarding...</div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      overflow: 'auto'
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '28px 40px',
          borderBottom: '1px solid #E5E7EB',
          background: '#FAFAFA'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: 0 }}>
              Assistant Onboarding
            </h2>
            <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500' }}>
                {completedSteps} of {totalSteps} steps completed
              </span>
              <span style={{ color: '#374151', fontSize: '13px', fontWeight: '600' }}>
                {progressPercent}%
              </span>
            </div>
            <div style={{
              height: '8px',
              background: '#E5E7EB',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${progressPercent}%`,
                background: '#3B82F6',
                borderRadius: '4px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Step Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {steps[currentStep].required && (
              <Lock size={18} color="#DC2626" />
            )}
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
              {steps[currentStep].title}
            </h3>
            {steps[currentStep].required && (
              <span style={{
                background: '#FEE2E2',
                color: '#DC2626',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                REQUIRED
              </span>
            )}
          </div>
        </div>

        {/* Step Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '32px 40px'
        }}>
          {renderStepContent(steps[currentStep], stepData[steps[currentStep].id] || {}, updateStepData)}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 40px',
            background: '#FEF2F2',
            borderTop: '1px solid #FCA5A5',
            borderBottom: '1px solid #FCA5A5'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} color="#DC2626" />
              <span style={{ color: '#DC2626', fontSize: '14px', fontWeight: '500' }}>
                {error}
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '20px 40px',
          borderTop: '1px solid #E5E7EB',
          background: '#FAFAFA',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
            disabled={currentStep === 0}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              color: currentStep === 0 ? '#9CA3AF' : '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <ChevronLeft size={16} />
            Back
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            {canSkipStep() && (
              <button
                onClick={handleSkipStep}
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Skip
              </button>
            )}
            
            <button
              onClick={handleCompleteStep}
              style={{
                padding: '10px 24px',
                background: steps[currentStep].required ? '#DC2626' : '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {currentStep === steps.length - 1 ? 'Complete Onboarding' : 'Continue'}
              {currentStep < steps.length - 1 && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Render step content based on step type
const renderStepContent = (step, data, updateStepData) => {
  switch (step.id) {
    case 'welcome':
      return <WelcomeStep />;
    
    case 'responsibilities':
      return <ResponsibilitiesStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'nda':
      return <NDAStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'security':
      return <SecurityStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'communication':
      return <CommunicationStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'walkthrough':
      return <WalkthroughStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'payment_info':
      return <PaymentInfoStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'availability':
      return <AvailabilityStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'tax_compliance':
      return <TaxComplianceStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    case 'final_confirmation':
      return <FinalConfirmationStep data={data} updateData={updateStepData} stepId={step.id} />;
    
    default:
      return <div>Step content not found</div>;
  }
};

// STEP 1: Welcome
const WelcomeStep = () => (
  <div style={{ maxWidth: '650px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
      Welcome to BM-Assist
    </h3>
    
    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.8', marginBottom: '20px' }}>
      Thank you for joining our team. As an assistant at BM-Assist, you play a vital role in supporting busy executives and ensuring their operations run smoothly. This is a <strong>trust-based, professional role</strong> that requires attention to detail, discretion, and proactive communication.
    </p>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
      <h4 style={{ color: '#1E40AF', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
        What You'll Be Doing
      </h4>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        Your daily work will include managing real tasks, coordinating schedules, handling communications, and supporting executives with time-sensitive requests. <strong>Accuracy, timeliness, and discretion are essential.</strong> The work you do directly impacts executive productivity and business operations.
      </p>
    </div>

    <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
      This Dashboard Is Your Central Hub For:
    </h4>
    <ul style={{ color: '#374151', fontSize: '15px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
      <li><strong>Managing tasks and priorities</strong> – Organize, track, and complete tasks across different timeframes</li>
      <li><strong>Coordinating schedules and events</strong> – Create and manage calendar events, appointments, and deadlines</li>
      <li><strong>Communicating with executives</strong> – Send updates, ask questions, and receive instructions</li>
      <li><strong>Accessing important resources</strong> – Find guides, templates, and reference materials</li>
    </ul>

    <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
      Our Core Expectations:
    </h4>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
      <div style={{ padding: '12px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
          Professionalism
        </p>
        <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Maintain the highest standards in all interactions. Your conduct represents BM-Assist and the executives you support.
        </p>
      </div>
      <div style={{ padding: '12px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
          Confidentiality
        </p>
        <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Protect sensitive information at all times. You may have access to private schedules, communications, and business details that must remain secure.
        </p>
      </div>
      <div style={{ padding: '12px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
          Accuracy
        </p>
        <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Double-check all work before submission. Small errors in scheduling, communication, or task management can have significant consequences.
        </p>
      </div>
      <div style={{ padding: '12px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
          Responsiveness
        </p>
        <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Communicate promptly and clearly. Executives depend on timely responses to keep their schedules and operations on track.
        </p>
      </div>
    </div>

    <p style={{ color: '#6B7280', fontSize: '14px', fontStyle: 'italic', lineHeight: '1.6' }}>
      This onboarding process will guide you through the policies, tools, and expectations that set you up for success in this role. Click "Continue" when you're ready to begin.
    </p>
  </div>
);

// STEP 2: Responsibilities  
const ResponsibilitiesStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '700px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Role Responsibilities & Boundaries
    </h3>
    
    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
      <p style={{ color: '#92400E', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>Why this matters:</strong> Clear boundaries protect both you and the executives you support. These guidelines help prevent misunderstandings, ensure accountability, and create a framework for effective collaboration. Understanding your scope of authority helps you work confidently without overstepping or making unauthorized decisions.
      </p>
    </div>

    <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
      What You ARE Responsible For:
    </h4>
    <ul style={{ color: '#374151', fontSize: '14px', lineHeight: '1.8', marginBottom: '20px', paddingLeft: '24px' }}>
      <li>Completing assigned tasks accurately and on time</li>
      <li>Communicating clearly about progress, delays, or concerns</li>
      <li>Following established procedures and guidelines</li>
      <li>Managing your assigned dashboard tools (tasks, calendar, messages)</li>
      <li>Escalating issues when appropriate</li>
    </ul>

    <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
      What You Are NOT Authorized To Do:
    </h4>
    <ul style={{ color: '#374151', fontSize: '14px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
      <li>Make decisions on behalf of executives without explicit permission</li>
      <li>Share executive or client information with unauthorized parties</li>
      <li>Access systems or data outside your assigned scope</li>
      <li>Represent yourself as an executive or decision-maker</li>
      <li>Commit the company or executives to agreements or obligations</li>
    </ul>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '24px' }}>
      <strong>If you're ever unsure about your authority or responsibility,</strong> ask before acting. It's always better to clarify expectations than to assume.
    </p>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' }}>
      Please acknowledge the following to confirm your understanding:
    </p>

    {[
      'I understand my role and responsibilities as an assistant at BM-Assist',
      'I understand I may have access to sensitive executive and client information',
      'I understand I must not act outside my assigned authority or scope',
      'I understand communication expectations and escalation procedures'
    ].map((text, i) => (
      <label key={i} style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        marginBottom: '12px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={data[`checkbox${i + 1}`] || false}
          onChange={(e) => updateData(stepId, `checkbox${i + 1}`, e.target.checked)}
          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <span style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>{text}</span>
      </label>
    ))}

    <div style={{ marginTop: '32px' }}>
      <label style={{ display: 'block', color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
        Type your full name to confirm <span style={{ color: '#DC2626' }}>*</span>
      </label>
      <input
        type="text"
        value={data.fullName || ''}
        onChange={(e) => updateData(stepId, 'fullName', e.target.value)}
        placeholder="Full Name"
        style={{
          width: '100%',
          padding: '12px',
          border: '1px solid #D1D5DB',
          borderRadius: '8px',
          fontSize: '15px'
        }}
      />
    </div>
  </div>
);

// STEP 3: NDA (COMPREHENSIVE)
const NDAStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '720px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Confidentiality & Non-Disclosure Agreement
    </h3>

    <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', padding: '18px', marginBottom: '24px' }}>
      <h4 style={{ color: '#991B1B', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
        Why Confidentiality Is Critical
      </h4>
      <p style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
        As an assistant, you will have access to <strong>private, time-sensitive, and business-critical information</strong> that must be protected. This may include:
      </p>
      <ul style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px', paddingLeft: '20px' }}>
        <li>Executive schedules, travel plans, and personal details</li>
        <li>Client names, communications, and project information</li>
        <li>Financial data, contracts, and business strategies</li>
        <li>Login credentials and system access</li>
      </ul>
      <p style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>This agreement exists to protect executives, clients, and you.</strong> It is standard business practice and reflects the trust placed in this role. Unauthorized disclosure or misuse of information can result in termination and legal consequences.
      </p>
    </div>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' }}>
      Please review the full agreement below. Take your time to read and understand each section before signing.
    </p>

    <div style={{
      background: '#F9FAFB',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '24px',
      maxHeight: '400px',
      overflowY: 'auto',
      fontSize: '14px',
      lineHeight: '1.8',
      color: '#374151'
    }}>
      <p style={{ fontWeight: '600', marginBottom: '16px' }}>
        CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT (NDA)
      </p>
      
      <p style={{ marginBottom: '16px' }}>
        This Confidentiality and Non-Disclosure Agreement ("Agreement") is entered into between BM-Assist (the "Company") and you (the "Assistant").
      </p>

      <p style={{ fontWeight: '600', marginTop: '20px', marginBottom: '8px' }}>
        1. Confidential Information
      </p>
      <p style={{ marginBottom: '16px' }}>
        During your role, you may have access to confidential, proprietary, or sensitive information including but not limited to:
      </p>
      <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
        <li>Executive information</li>
        <li>Client data</li>
        <li>Login credentials</li>
        <li>Financial, scheduling, communication, or operational details</li>
        <li>Any non-public business information</li>
      </ul>

      <p style={{ fontWeight: '600', marginTop: '20px', marginBottom: '8px' }}>
        2. Obligations
      </p>
      <p style={{ marginBottom: '8px' }}>You agree to:</p>
      <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
        <li>Keep all confidential information strictly confidential</li>
        <li>Not disclose, share, copy, or distribute confidential information to any third party</li>
        <li>Use confidential information solely for the purpose of performing your assigned duties</li>
        <li>Not access or use information beyond what is necessary for your role</li>
      </ul>

      <p style={{ fontWeight: '600', marginTop: '20px', marginBottom: '8px' }}>
        3. Security
      </p>
      <p style={{ marginBottom: '8px' }}>You agree to:</p>
      <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
        <li>Not share login credentials</li>
        <li>Use secure devices and networks</li>
        <li>Immediately report any suspected security breach or unauthorized access</li>
      </ul>

      <p style={{ fontWeight: '600', marginTop: '20px', marginBottom: '8px' }}>
        4. Term
      </p>
      <p style={{ marginBottom: '16px' }}>
        This Agreement applies during your engagement and continues indefinitely after termination of your role.
      </p>

      <p style={{ fontWeight: '600', marginTop: '20px', marginBottom: '8px' }}>
        5. Breach
      </p>
      <p style={{ marginBottom: '16px' }}>
        Unauthorized disclosure or misuse of confidential information may result in immediate termination and may expose you to legal action.
      </p>

      <p style={{ marginTop: '20px', fontWeight: '600' }}>
        By agreeing below, you acknowledge that you have read, understand, and agree to be bound by this Agreement.
      </p>
    </div>

    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      background: '#FEF2F2',
      border: '2px solid #FCA5A5',
      borderRadius: '8px',
      marginBottom: '24px',
      cursor: 'pointer'
    }}>
      <input
        type="checkbox"
        checked={data.ndaAgreed || false}
        onChange={(e) => updateData(stepId, 'ndaAgreed', e.target.checked)}
        style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
      />
      <span style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
        I agree to the Confidentiality & Non-Disclosure Agreement
      </span>
    </label>

    <div>
      <label style={{ display: 'block', color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
        Type your full legal name <span style={{ color: '#DC2626' }}>*</span>
      </label>
      <input
        type="text"
        value={data.legalName || ''}
        onChange={(e) => updateData(stepId, 'legalName', e.target.value)}
        placeholder="Legal Name"
        style={{
          width: '100%',
          padding: '12px',
          border: '1px solid #D1D5DB',
          borderRadius: '8px',
          fontSize: '15px'
        }}
      />
      <p style={{ color: '#6B7280', fontSize: '12px', marginTop: '6px' }}>
        Signed on: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
    </div>
  </div>
);

// STEP 4: Security
const SecurityStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '700px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Security & Access Rules
    </h3>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '18px', marginBottom: '24px' }}>
      <h4 style={{ color: '#1E40AF', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
        Why Security Matters
      </h4>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
        Your account provides access to sensitive executive and client information. <strong>Protecting this access is a shared responsibility.</strong> Security breaches can lead to:
      </p>
      <ul style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px', paddingLeft: '20px' }}>
        <li>Unauthorized access to private schedules and communications</li>
        <li>Identity theft or impersonation</li>
        <li>Loss of client trust and business relationships</li>
        <li>Legal and financial consequences</li>
      </ul>
    </div>

    <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
      Practical Security Guidance:
    </h4>
    
    <div style={{ marginBottom: '20px', padding: '14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
        🔒 Never Share Your Login Credentials
      </p>
      <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
        <strong>Even with trusted colleagues.</strong> If someone needs access, they should request their own account. Shared credentials make it impossible to track who did what and can lead to accountability issues.
      </p>
    </div>

    <div style={{ marginBottom: '20px', padding: '14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
        🌐 Use Secure Devices and Networks
      </p>
      <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
        Avoid public Wi-Fi when handling sensitive information. If you must use shared networks, use a VPN. Ensure your device has up-to-date antivirus software and OS updates.
      </p>
    </div>

    <div style={{ marginBottom: '20px', padding: '14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
        🚪 Always Log Out on Shared Devices
      </p>
      <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
        If you access the dashboard from a library, office, or any shared computer, <strong>always log out completely</strong> when finished. Close browser windows and clear browsing data if possible.
      </p>
    </div>

    <div style={{ marginBottom: '24px', padding: '14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px' }}>
      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
        ⚠️ Report Suspicious Activity Immediately
      </p>
      <p style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
        If you notice anything unusual—unexpected password resets, unfamiliar login locations, strange messages, or unauthorized changes—<strong>report it immediately</strong> so we can secure accounts and investigate. Early reporting prevents bigger problems.
      </p>
    </div>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' }}>
      Please acknowledge the following security commitments:
    </p>

    {[
      'I will not share my login credentials with anyone',
      'I will use secure devices and networks when accessing the dashboard',
      'I will log out of the dashboard when using shared or public devices',
      'I will immediately report any suspicious activity or security concerns'
    ].map((text, i) => (
      <label key={i} style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        marginBottom: '12px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={data[`checkbox${i + 1}`] || false}
          onChange={(e) => updateData(stepId, `checkbox${i + 1}`, e.target.checked)}
          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <span style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>{text}</span>
      </label>
    ))}
  </div>
);

// STEP 5: Communication
const CommunicationStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '720px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Communication & Escalation Guidelines
    </h3>

    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
      <p style={{ color: '#166534', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>Why clear communication matters:</strong> Effective communication prevents delays, reduces stress for everyone involved, and ensures that urgent issues get the attention they need. Knowing when and how to communicate helps you work efficiently without over- or under-escalating.
      </p>
    </div>

    <div style={{ marginBottom: '24px' }}>
      <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '14px' }}>
        When to Message vs. Escalate
      </h4>
      
      <div style={{ padding: '14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '12px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          ✉️ Use Regular Messaging For:
        </p>
        <ul style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', margin: 0 }}>
          <li>Routine status updates</li>
          <li>General questions that aren't time-sensitive</li>
          <li>Task coordination and clarifications</li>
          <li>Scheduling non-urgent requests</li>
        </ul>
      </div>

      <div style={{ padding: '14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px' }}>
        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          🚨 Escalate Immediately For:
        </p>
        <ul style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.7', paddingLeft: '20px', marginBottom: '8px' }}>
          <li><strong>Client emergencies</strong> or time-sensitive issues affecting business operations</li>
          <li><strong>System failures</strong> that prevent you from completing critical tasks</li>
          <li><strong>Security concerns</strong> such as suspected unauthorized access</li>
          <li><strong>Urgent executive requests</strong> that require immediate attention</li>
          <li><strong>Any situation where delay could cause significant harm or loss</strong></li>
        </ul>
        <p style={{ color: '#7F1D1D', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>
          When in doubt, escalate. It's better to raise a concern than to let an issue worsen.
        </p>
      </div>
    </div>

    <div style={{ marginBottom: '24px' }}>
      <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '14px' }}>
        Expected Response Times
      </h4>
      <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
        Response times help set clear expectations and ensure important matters don't fall through the cracks:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ padding: '12px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '6px' }}>
          <strong style={{ color: '#991B1B', fontSize: '14px' }}>Urgent:</strong>
          <span style={{ color: '#7F1D1D', fontSize: '14px' }}> Within 1 hour during working hours</span>
          <p style={{ color: '#7F1D1D', fontSize: '13px', margin: '4px 0 0 0' }}>
            Examples: Executive stranded, client crisis, security breach
          </p>
        </div>
        <div style={{ padding: '12px', background: '#FEF9E7', border: '1px solid #FDE68A', borderRadius: '6px' }}>
          <strong style={{ color: '#92400E', fontSize: '14px' }}>High Priority:</strong>
          <span style={{ color: '#78350F', fontSize: '14px' }}> Within 4 hours</span>
          <p style={{ color: '#78350F', fontSize: '13px', margin: '4px 0 0 0' }}>
            Examples: Same-day meeting prep, urgent schedule changes
          </p>
        </div>
        <div style={{ padding: '12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
          <strong style={{ color: '#374151', fontSize: '14px' }}>Normal:</strong>
          <span style={{ color: '#6B7280', fontSize: '14px' }}> Within 24 hours</span>
          <p style={{ color: '#6B7280', fontSize: '13px', margin: '4px 0 0 0' }}>
            Examples: Routine updates, general questions, non-urgent tasks
          </p>
        </div>
      </div>
    </div>

    <div style={{ marginBottom: '24px' }}>
      <h4 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
        Who You Can Contact
      </h4>
      <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.7', marginBottom: '12px' }}>
        You are authorized to communicate with the following people through this system:
      </p>
      <ul style={{ color: '#374151', fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
        <li><strong>Your assigned executives</strong> – for task coordination, updates, and questions</li>
        <li><strong>BM-Assist administrators</strong> – for technical support, policy questions, and escalations</li>
        <li><strong>Other assistants</strong> – for collaboration and coordination when appropriate</li>
      </ul>
      <p style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic', lineHeight: '1.6' }}>
        If you need to contact someone outside this list (such as a client), get explicit approval from your assigned executive first.
      </p>
    </div>

    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
      borderRadius: '8px',
      cursor: 'pointer'
    }}>
      <input
        type="checkbox"
        checked={data.agreed || false}
        onChange={(e) => updateData(stepId, 'agreed', e.target.checked)}
        style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
      />
      <span style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
        I understand and agree to follow these communication guidelines
      </span>
    </label>
  </div>
);

// STEP 6: Walkthrough
const WalkthroughStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '720px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Dashboard Orientation
    </h3>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        This is your guided orientation to the dashboard. Understanding how each area works will help you stay organized, communicate effectively, and complete tasks efficiently. <strong>Take a moment to review each feature and its purpose.</strong>
      </p>
    </div>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px', fontWeight: '500' }}>
      Key Features and How to Use Them:
    </p>

    <div style={{ marginBottom: '24px' }}>
      {[
        { 
          title: 'Dashboard Overview', 
          desc: "Your home page and command center. This is where you'll see at-a-glance metrics—overdue tasks, today's priorities, upcoming events, and recent messages. Use it to quickly assess what needs your attention.",
          purpose: 'Start here each day to prioritize your work.'
        },
        { 
          title: 'To-Do Center', 
          desc: "Your task management hub. Tasks are organized into columns: Today, Tomorrow, This Week, Next Week, and Backlog. You can drag and drop tasks to reschedule them as priorities change. Each task can have a priority level, due date, and assigned executive.",
          purpose: 'Use this daily to track and complete assigned work. Keep "Today" tasks up to date.'
        },
        { 
          title: 'Calendar', 
          desc: "View and manage events, appointments, and deadlines. You can create calendar events for yourself and executives. Treat this as a real calendar—accuracy is critical. A scheduling mistake can have serious consequences.",
          purpose: 'Coordinate schedules, set reminders, and avoid conflicts.'
        },
        { 
          title: 'Messages', 
          desc: "Direct communication with your assigned executives and other assistants. Use this for updates, questions, clarifications, and coordination. Messages should be professional, clear, and concise.",
          purpose: 'Stay in touch without overloading email. Response times matter.'
        },
        { 
          title: 'Executive Hub', 
          desc: "Access profiles, preferences, and important information about the executives you support. This might include contact preferences, meeting habits, travel preferences, or important dates. Review this regularly to provide better support.",
          purpose: 'Understand how your executives work so you can anticipate their needs.'
        },
        { 
          title: 'Resources & Tools', 
          desc: "Find guides, templates, and documentation to help you complete tasks. This may include reference materials, training docs, or frequently used templates. Bookmark items you use often.",
          purpose: "Learn and improve. Don't reinvent the wheel—use what's available."
        }
      ].map((item, i) => (
        <div key={i} style={{
          padding: '16px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            {item.title}
          </h4>
          <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.7', marginBottom: '8px' }}>
            {item.desc}
          </p>
          <p style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>
            <strong>Purpose:</strong> {item.purpose}
          </p>
        </div>
      ))}
    </div>

    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
      <p style={{ color: '#92400E', fontSize: '13px', lineHeight: '1.7', margin: 0 }}>
        <strong>Pro Tip:</strong> Spend your first few days exploring each area. The more familiar you are with the dashboard, the more efficient and confident you'll be in your role.
      </p>
    </div>

    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
      borderRadius: '8px',
      cursor: 'pointer'
    }}>
      <input
        type="checkbox"
        checked={data.completed || false}
        onChange={(e) => updateData(stepId, 'completed', e.target.checked)}
        style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
      />
      <span style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
        I have reviewed the system walkthrough and understand the dashboard features
      </span>
    </label>
  </div>
);

// STEP 7: Payment Info (REQUIRED)
const PaymentInfoStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '650px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
      Payment Information
    </h3>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>This information is required for compensation setup.</strong> We need accurate payment details to ensure you receive timely payment for your work. This information is stored securely and used solely for payment processing purposes.
      </p>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          Preferred Payment Method <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <select
          value={data.paymentMethod || ''}
          onChange={(e) => updateData(stepId, 'paymentMethod', e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        >
          <option value="">Select payment method...</option>
          <option value="ach">ACH / Bank Transfer</option>
          <option value="paypal">PayPal</option>
          <option value="cashapp">Cash App</option>
          <option value="zelle">Zelle</option>
          <option value="venmo">Venmo</option>
          <option value="other">Other</option>
        </select>
      </div>

      {(data.paymentMethod === 'paypal' || data.paymentMethod === 'cashapp') && (
        <div>
          <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
            {data.paymentMethod === 'paypal' ? 'PayPal Email' : 'Cash App Tag'} <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            type="text"
            value={data.handle || ''}
            onChange={(e) => updateData(stepId, 'handle', e.target.value)}
            placeholder={data.paymentMethod === 'paypal' ? 'your@email.com' : '$yourcashtag'}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>
      )}

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          Legal Name (for payments) <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <input
          type="text"
          value={data.legalName || ''}
          onChange={(e) => updateData(stepId, 'legalName', e.target.value)}
          placeholder="Full legal name as it appears on official documents"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
      </div>

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
          Additional Notes (Optional)
        </label>
        <textarea
          value={data.notes || ''}
          onChange={(e) => updateData(stepId, 'notes', e.target.value)}
          placeholder="Any additional payment-related information or special instructions"
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
      </div>
    </div>
  </div>
);

// STEP 8: Availability (REQUIRED)
const AvailabilityStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '650px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
      Availability & Working Hours
    </h3>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>Understanding your availability helps us assign appropriate tasks and set realistic expectations.</strong> This information allows executives to know when you're typically available and helps prevent scheduling conflicts or unrealistic response time expectations.
      </p>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          Time Zone <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <select
          value={data.timezone || ''}
          onChange={(e) => updateData(stepId, 'timezone', e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        >
          <option value="">Select timezone...</option>
          <option value="US/Eastern">US Eastern (ET)</option>
          <option value="US/Central">US Central (CT)</option>
          <option value="US/Mountain">US Mountain (MT)</option>
          <option value="US/Pacific">US Pacific (PT)</option>
          <option value="Europe/London">UK (GMT/BST)</option>
          <option value="Europe/Paris">Central Europe (CET)</option>
          <option value="Asia/Dubai">UAE (GST)</option>
          <option value="Asia/Manila">Philippines (PHT)</option>
          <option value="Asia/Kolkata">India (IST)</option>
          <option value="other">Other (specify in notes)</option>
        </select>
      </div>

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
          Typical Working Hours <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <input
          type="text"
          value={data.workingHours || ''}
          onChange={(e) => updateData(stepId, 'workingHours', e.target.value)}
          placeholder="e.g., 9:00 AM - 5:00 PM EST"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
        <p style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
          Provide your usual availability window (e.g., 9am-5pm, 10am-6pm)
        </p>
      </div>

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
          Days Available <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
            <label key={day} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: data.daysAvailable?.includes(day) ? '#EFF6FF' : '#F9FAFB',
              border: data.daysAvailable?.includes(day) ? '1px solid #3B82F6' : '1px solid #E5E7EB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={data.daysAvailable?.includes(day) || false}
                onChange={(e) => {
                  const current = data.daysAvailable || [];
                  const updated = e.target.checked
                    ? [...current, day]
                    : current.filter(d => d !== day);
                  updateData(stepId, 'daysAvailable', updated);
                }}
                style={{ cursor: 'pointer' }}
              />
              {day}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
          Emergency Availability <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <p style={{ color: '#6B7280', fontSize: '13px', lineHeight: '1.6', marginBottom: '10px' }}>
          Are you available to respond to urgent matters outside your typical working hours if absolutely necessary? (This does not mean you are always on-call, but indicates whether you can occasionally be reached for true emergencies.)
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: data.emergencyAvailable === 'yes' ? '#F0FDF4' : '#F9FAFB',
            border: data.emergencyAvailable === 'yes' ? '1px solid #10B981' : '1px solid #E5E7EB',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px'
          }}>
            <input
              type="radio"
              name="emergency"
              checked={data.emergencyAvailable === 'yes'}
              onChange={() => updateData(stepId, 'emergencyAvailable', 'yes')}
              style={{ cursor: 'pointer' }}
            />
            Yes
          </label>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: data.emergencyAvailable === 'no' ? '#FEF2F2' : '#F9FAFB',
            border: data.emergencyAvailable === 'no' ? '1px solid #DC2626' : '1px solid #E5E7EB',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px'
          }}>
            <input
              type="radio"
              name="emergency"
              checked={data.emergencyAvailable === 'no'}
              onChange={() => updateData(stepId, 'emergencyAvailable', 'no')}
              style={{ cursor: 'pointer' }}
            />
            No
          </label>
        </div>
      </div>

      <div>
        <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
          Additional Notes (Optional)
        </label>
        <textarea
          value={data.availabilityNotes || ''}
          onChange={(e) => updateData(stepId, 'availabilityNotes', e.target.value)}
          placeholder="Any additional information about your availability, planned time off, or special considerations"
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
      </div>
    </div>
  </div>
);

// STEP 9: Tax Compliance (OPTIONAL)
const TaxComplianceStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '650px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
      Tax & Compliance Information (Optional)
    </h3>

    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
      <p style={{ color: '#166534', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        <strong>This step is optional and administrative only.</strong> If you are working as an independent contractor, we may need tax documentation for compliance purposes. This information is used solely for tax reporting and does not affect your onboarding or work status.
      </p>
    </div>

    <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
      You can skip this step now and provide documentation later if requested. If you'd like to acknowledge your contractor status now, you can do so below.
    </p>

    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={data.contractorStatus || false}
          onChange={(e) => updateData(stepId, 'contractorStatus', e.target.checked)}
          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <span style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>
          I confirm I am working as an independent contractor
        </span>
      </label>
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={data.taxResponsibility || false}
          onChange={(e) => updateData(stepId, 'taxResponsibility', e.target.checked)}
          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <span style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>
          I understand I am responsible for my own taxes as an independent contractor
        </span>
      </label>
    </div>

    <div style={{
      padding: '16px',
      background: '#FFFBEB',
      border: '1px solid #FDE68A',
      borderRadius: '8px'
    }}>
      <p style={{ color: '#92400E', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
        <strong>Note:</strong> If you are a U.S.-based contractor, we may request a W-9 form for tax reporting purposes. This can be provided separately after onboarding.
      </p>
    </div>
  </div>
);

// STEP 9: Final Confirmation
const FinalConfirmationStep = ({ data, updateData, stepId }) => (
  <div style={{ maxWidth: '700px' }}>
    <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
      Final Confirmation & Readiness
    </h3>

    <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '18px', marginBottom: '24px' }}>
      <h4 style={{ color: '#1E40AF', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
        You're Almost Ready to Begin
      </h4>
      <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
        You've completed the required onboarding steps and reviewed important policies. By confirming below, you're acknowledging that you understand the expectations, responsibilities, and guidelines for this role. <strong>This is your commitment to uphold these standards as a trusted member of the team.</strong>
      </p>
    </div>

    <p style={{ color: '#374151', fontSize: '15px', lineHeight: '1.7', marginBottom: '24px' }}>
      Please confirm the following statements to complete your onboarding and unlock full dashboard access:
    </p>

    {[
      'I confirm all information I have provided is accurate and complete',
      'I am ready to begin my role as an assistant and will uphold all policies and agreements'
    ].map((text, i) => (
      <label key={i} style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        background: '#F0FDF4',
        border: '1px solid #BBF7D0',
        borderRadius: '8px',
        marginBottom: '12px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={data[`checkbox${i + 1}`] || false}
          onChange={(e) => updateData(stepId, `checkbox${i + 1}`, e.target.checked)}
          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <span style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>{text}</span>
      </label>
    ))}

    <div style={{
      marginTop: '32px',
      padding: '24px',
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <CheckCircle size={48} color="#10B981" style={{ margin: '0 auto 16px' }} />
      <p style={{ color: '#0A0A0A', fontSize: '17px', fontWeight: '600', marginBottom: '12px' }}>
        Welcome to the Team!
      </p>
      <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.8', marginBottom: '16px' }}>
        You're now ready to begin your role as an assistant at BM-Assist. Your dashboard access will be fully unlocked when you complete this step. <strong>Expectations are clear, policies are understood, and support is available whenever you need it.</strong>
      </p>
      <p style={{ color: '#6B7280', fontSize: '13px', lineHeight: '1.6', margin: 0, fontStyle: 'italic' }}>
        If you have questions or need assistance at any time, don't hesitate to reach out to your assigned executive or a BM-Assist administrator. We're here to help you succeed.
      </p>
    </div>
  </div>
);

export default OnboardingWizard;
